import {
    type Child,
    childListSchema,
    childPath,
    childSchema,
    childrenPath,
    type CreateChildInput,
    type UpdateChildInput,
} from '@baby-tracker/contracts';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiGet, apiRequest } from '../lib/api-client.js';
import { queryKeys } from '../lib/query-keys.js';

export function useChildren(
    familyId: string | undefined,
    includeArchived: boolean,
): UseQueryResult<Child[]> {
    return useQuery({
        queryKey: queryKeys.children(familyId ?? '', includeArchived),
        enabled: familyId !== undefined,
        queryFn: ({ signal }) =>
            apiGet(
                `${childrenPath(familyId ?? '')}${includeArchived ? '?includeArchived=true' : ''}`,
                childListSchema,
                { signal },
            ),
    });
}

export function useCreateChild(
    familyId: string,
): UseMutationResult<Child, Error, CreateChildInput> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: CreateChildInput) =>
            apiRequest('POST', childrenPath(familyId), childSchema, { body: input }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['children', familyId] });
        },
    });
}

export function useUpdateChild(
    familyId: string,
): UseMutationResult<Child, Error, { childId: string; body: UpdateChildInput }> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: { childId: string; body: UpdateChildInput }) =>
            apiRequest('PATCH', childPath(input.childId), childSchema, { body: input.body }),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['children', familyId] });
        },
    });
}

export function useArchiveChild(familyId: string): UseMutationResult<Child, Error, Child> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (child: Child) => apiRequest('DELETE', childPath(child.id), childSchema),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['children', familyId] });
        },
    });
}
